// src/pages/SubscriberDashboardPage.jsx
// Route: /subscriber/dashboard
//
// No Figma frame exists for this screen — see Gap Report shared with Deepu.
// Restyled onto the Navigator portal's shared classes (.page-header, .card,
// .badge, .loading-state, .alert) so this page reads like the rest of the
// app instead of a one-off inline-styled screen. Empty state reuses the
// existing EmptyState.jsx component per Section 5.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import EmptyState from '../components/EmptyState';
import RiskBadge from '../components/RiskBadge';
import { displayPatientCode } from '../utils/patientCode';

function BeneficiaryCard({ beneficiary, onClick }) {
  const code = displayPatientCode(beneficiary.patientCode);
  return (
    <div
      className="card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
      style={{ cursor: 'pointer' }}
    >
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
        color: 'var(--text-primary)', marginBottom: 8,
      }}>
        {beneficiary.firstName} {beneficiary.lastName}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <RiskBadge level={beneficiary.latestRiskLevel} />
        {beneficiary.previuAge != null && (
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Previu Age {beneficiary.previuAge}
          </span>
        )}
        {code && <span className="mono" data-testid="beneficiary-code">{code}</span>}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {beneficiary.carePlanStatus === 'ACTION_PLAN_SET' ? 'Action plan set' : 'No action plan yet'}
        {' · '}
        {beneficiary.openLabOrders} lab order{beneficiary.openLabOrders === 1 ? '' : 's'} in progress
      </div>
    </div>
  );
}

export default function SubscriberDashboardPage() {
  const { subscriber } = useSubscriber();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const loadBeneficiaries = useCallback(async () => {
    if (!subscriber?.subscriberId) return;
    setLoading(true); setError('');
    try {
      const res = await subscriberAPI.beneficiaries(subscriber.subscriberId);
      setBeneficiaries(res.data || []);
    } catch (err) {
      setError('Could not load your family’s data. Please try again.');
      toast.error('Could not load your family’s data.');
    } finally {
      setLoading(false);
    }
  }, [subscriber?.subscriberId, toast]);

  useEffect(() => { loadBeneficiaries(); }, [loadBeneficiaries]);

  const noActivePlan = subscriber?.subscriptionStatus !== 'ACTIVE';

  return (
    <SubscriberAppShell breadcrumbs={[{ label: 'My Family' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">My Family</div>
          <div className="page-subtitle">Hi {subscriber?.firstName}, here's the latest for everyone enrolled.</div>
        </div>
      </div>

      {noActivePlan && (
        <div className="alert fade-in-up" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>
          You don't have an active plan yet.{' '}
          <button type="button" onClick={() => navigate('/subscriber/plan')} style={{
            background: 'none', border: 'none', color: 'var(--brand-dark)', fontWeight: 700,
            textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0,
          }}>
            Choose a plan
          </button>
        </div>
      )}

      {!noActivePlan && (
        <div className="alert fade-in-up" style={{
          background: 'var(--green-bg)', color: 'var(--green)',
          justifyContent: 'space-between',
        }}>
          <span>
            Plan: <strong>{subscriber?.subscriptionPlan === 'GUARDIAN_SENTINEL' ? 'Guardian Sentinel' : 'Guardian'}</strong> — Active
          </span>
          <button type="button" onClick={() => navigate('/subscriber/plan')} style={{
            background: 'none', border: 'none', color: 'var(--brand-dark)', fontWeight: 600,
            textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0, fontSize: 13,
          }}>
            Manage plan
          </button>
        </div>
      )}

      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          Loading your family…
        </div>
      )}

      {!loading && error && (
        <EmptyState
          icon="alert"
          title="Something went wrong"
          message={error}
          action={{ label: 'Try again', onClick: loadBeneficiaries }}
        />
      )}

      {!loading && !error && beneficiaries.length === 0 && (
        <EmptyState
          icon="people"
          title="No family members enrolled yet"
          message={noActivePlan
            ? "Choose a plan to start adding family members."
            : "Add your first family member, or your Navigator can enroll them for you."}
          action={noActivePlan ? undefined : { label: 'Add family member', onClick: () => navigate('/subscriber/add-beneficiary') }}
        />
      )}

      {!loading && !error && beneficiaries.length > 0 && beneficiaries.length < (subscriber?.maxBeneficiaries || 1) && (
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/subscriber/add-beneficiary')} style={{
          border: '1.5px dashed var(--border)', width: '100%', marginBottom: 16,
        }}>
          + Add another family member
        </button>
      )}

      {!loading && !error && beneficiaries.map((b) => (
        <BeneficiaryCard key={b.patientId} beneficiary={b}
          onClick={() => navigate(`/subscriber/beneficiaries/${b.patientId}`)} />
      ))}
    </SubscriberAppShell>
  );
}
